
import {from as observableFrom} from 'rxjs';

import {tap, delay, map, switchMap, filter} from 'rxjs/operators';
import {
	AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, EventEmitter, Inject, Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {
	WINDOW_LISTENER_TOKEN, WindowEvent,
	WindowHostListenerService
} from "../../../../window/window.host.listener.service";
import {SearchBoxComponent} from "../../";

declare type T = any;
export declare type filterFn = (item: T, value: string) => boolean;
export declare type focusFn = () => void;
export declare type AutocompleteData = {
	data: T[];
	moreResults: boolean;
};

@UntilDestroy()
@Component({
	selector: 'cp-autocomplete',
	templateUrl: "./autocomplete.html",
	styleUrls: ['./autocomplete.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() focusOnInit:boolean = false; // whether to focus on init
	@Input() searchString: string;
	@Input() data: T[];
	@Input() filter: filterFn;
	@Input() focusFirstItem: focusFn;
	@Input() maxItems?: number;
	@Input() minChars: number;
	@Input() placeholder: string[] = [];
	@Input() navigable: boolean = true;
	@Input() defaultSearchString: string;
	@Input() enableSearchBoxClearButton: boolean = false;
	@Input() customButton: {class: string};
	@Input() readOnly: boolean;
	@Input() serverSideFilter: boolean;
	@Input() headerTitle: string;
	@Input() headerTitleParams: any;
	@Input() placeholderTranslationKey: string;

	@Output('focus') focusChange = new EventEmitter();
	@Output('blur') blurChange = new EventEmitter();
	@Output() dataChange = new EventEmitter<AutocompleteData>();
	@Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();
	@Output() filterChanged: EventEmitter<{filter: string, selectResult: boolean}> = new EventEmitter<{filter: string, selectResult: boolean}>();

	@Output() searchStringChange: EventEmitter<string> = new EventEmitter();
	@Output() onClearSearchBox: EventEmitter<any> = new EventEmitter();
	@Output() customButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

	@ViewChild("searchBox",{static:false}) searchBox: SearchBoxComponent;

	@ContentChild("autocompleteHeaderTemplate ",{static:false}) public headerTemplate;
	@ContentChild("autocompleteNoDataTemplate",{static:false}) public noDataTemplate;
	@ContentChild("autocompleteItemTemplate",{static:false}) public itemTemplate;
	@ContentChild("autocompleteContentTemplate",{static:false}) public contentTemplate;

	readonly UP_CODE: number = 38;
	readonly DOWN_CODE: number = 40;
	readonly ESC_CODE: number = 27;
	readonly ENTER_CODE: number = 13;

	public get text(): string {
		return (this.searchBox && this.searchBox.searchString) ? this.searchBox.searchString.trim() : '';
	}

	public viewData: T[];
	public isOpen: boolean;
	public navIndex: number;

	public _isFocus: boolean;

	constructor(@Inject(WINDOW_LISTENER_TOKEN) private windowHostListenerService: WindowHostListenerService,
				private changeDetector: ChangeDetectorRef) {
	}

	public ngOnInit(): void {
		if (this.navigable) {
			this.navIndex = -1;
			this.windowHostListenerService.getObservable<KeyboardEvent>(WindowEvent.keydown).pipe(
				filter((e) => this.isOpen &&
					(e.keyCode === this.ESC_CODE || e.keyCode === this.UP_CODE || e.keyCode === this.DOWN_CODE || e.keyCode === this.ENTER_CODE)))
				.pipe(untilDestroyed(this))
				.subscribe((e) => this.handleNavigate(e));
		}
	}

	public ngAfterViewInit(): void {
		this.searchBox.search.asObservable().pipe(
			filter(() => !!this.data),
			tap((value) => {
				this.searchStringChange.emit(_.trim(value));
				if (value.length < this.minChars) {
					this.itemSelected.emit(null);
				}
				return value.length < this.minChars ? this.close() : null;
			}),
			switchMap(value => observableFrom([this.data]).pipe(
				filter(() => value.length >= this.minChars),
				delay(300),
				map((data) => this._filter(data, value, this.serverSideFilter)),)),)
			.pipe(untilDestroyed(this))
			.subscribe((res) => {
				if (!this.serverSideFilter) {
					this.viewData = res.data;
					this.open();
				}
				this.dataChange.emit(res);
			});
	}

	public ngOnDestroy(): void { }

	public open() {
		if (!this.isOpen) {
			this.isOpen = true;
			this.changeDetector.markForCheck();
		}
	}

	public close() {
		if (this.isOpen) {
			this.isOpen = false;
			this.resetNavigate();
			this.changeDetector.markForCheck();
		}
	}

	public filterChange(value:string) {
		this.searchBox.search.emit(value);
	}

	get isFocus(): boolean {
		return this._isFocus;
	}

	public focus() {
		this._isFocus = true;
		this.searchBox.focus();
	}

	public blur() {
		this._isFocus = false;
		this.searchBox.blur();
	}

	public onBlur() {
		if (this.searchBox) {
			this.searchBox.reset(this.defaultSearchString);
		}
		this.blurChange.emit();
	}

	public reset(searchResult?: string) {
		this.searchBox.reset(searchResult);
		this.resetNavigate();
	}

	public haveData() {
		return this.viewData && this.viewData.length;
	}

	public onClearSearchBoxHandler() {
		this.close();
		this.onClearSearchBox.emit();
	}

	public onSearchBoxClick() {
		if (!this.isOpen) {
			// note this was tested as working only in serverSide filtering option.
			// may need to change condition
			if (this.defaultSearchString) {
				if (this.haveData()) {
					let filteredData = this._filter(this.viewData, this.searchString, false);
					this.viewData = filteredData.data;
					if (filteredData.data.length === 1) {
						this.itemSelected.emit(filteredData.data[0]);
					}
				} else {
					if (this.data && this.data.length) {
						this.viewData = this.data;
					} else {
						if (this.serverSideFilter) {
							this.filterChanged.emit({filter: this.defaultSearchString, selectResult: true});
						}
					}
				}
				setTimeout(() => {
					this.open();
				}, 0);
			} else {
				if (this.serverSideFilter) {
					this.filterChanged.emit(null);
				}
			}
		}
	}

	private _filter(data: T[], value: string, useServerSideFilter: boolean): AutocompleteData {
		let retVal = [];

		if (this.serverSideFilter && useServerSideFilter) {
			this.filterChanged.emit({filter: value, selectResult: false});
			return {data: this.data, moreResults: false};
		} else {
			for (let i = 0; i < data.length; i++) {
				if (!this.filter || this.filter(data[i], value)) {
					if (this.maxItems && this.maxItems === retVal.length) {
						return {data: retVal, moreResults: true};
					}
					retVal.push(data[i]);
				}
			}

			return {data: retVal, moreResults: false};
		}
	}

	private resetNavigate() {
		if (this.navigable) {
			this.navIndex = -1;
		}
	}

	private handleNavigate(e) {
		switch (e.keyCode) {
			case  this.UP_CODE:
				this.navIndex = Math.max(0, this.navIndex-1);
				break;
			case this.DOWN_CODE:
				this.navIndex = Math.min(this.navIndex+1, this.viewData.length - 1);

				if (this.navIndex === 0 && this.focusFirstItem) {
					this.focusFirstItem();
				}
				break;
			case this.ESC_CODE:
				this.close();
				this.focus();
				break;
			case this.ENTER_CODE:
				if (this.navIndex > -1) {
					this.itemSelected.emit(this.viewData[this.navIndex]);
				}
				break;
		}
		this.changeDetector.markForCheck();
	}
}
